// 生成複習考卷 — 個人化複習卷入口(C task,接在 A 自動批改 + B 會教錯題本之後)。
//
// 改版重點(2026-06-08):從「依賴 worker LLM-Wiki + 家庭碼」改成「純學習側、
// 離線可組」的個人化複習卷:
//   1. 讀本機 progress(`mistakes` 未 retried + `studentKPs` box 低 / 到期),
//      `pickWeakKPs` 跨科挑「最該複習」的弱點。
//   2. 每個弱 KP = 原錯題本人 + 1~2 題類似題(數學走 generator;其他科走 LLM,
//      LLM 不可用優雅降級成只放原錯題)。
//   3. 組成「按科分大題」的考卷:可在新視窗列印(`generatePersonalReviewHtml`),
//      也可在畫面內逐題作答 + 「顯示解答」。**不是一問一答對話。**
//   4. 做完批改回流:畫面內作答 → 答對 `recordAttemptV2` 升 box,答錯 `recordMistake`
//      回流錯題本;或印出後走拍照自動批改(`grade-review`,沿用 storeReviewPaper)。
//   5. 空錯題本 → 用 box 弱點;完全沒弱點 → 友善提示先去練習。
//
// 學習/遊戲分離鐵則:只讀寫學習側資料(錯題 / box / factionXP),絕不碰戰鬥數值、
// commanderXp。專業介面:學術風格設計系統,純文字、無音效、無 mascot、無彩光。

import { loadProgress, saveProgress } from '../../shared/progress';
import { recordMistake, deriveQuestionId } from '../../shared/mistake-log';
import { findOrCreateKP, recordAttemptV2, advancePracticePaperCount } from '../../shared/student-kp';
import type { Subject, PlayerProgress } from '../../shared/types';
import { ensureAcademicTheme } from '../ui/theme';
import { generatePersonalReviewHtml } from '../printable/pdf-gen';
import {
  buildPersonalReviewPaper,
  toReviewPaper,
  paperHeadline,
} from './personal-review';
import type { PersonalReviewPaper, ReviewItem } from './personal-review';
import type { ReviewPaper } from './types';

// ── P3 閉環:生卷後把攤平的 ReviewPaper 存進 localStorage,供「拍照批改複習考卷」
// 取回對應卷號的 answerKey / 題目。最近 5 張(by paperId,新的在前)。
// 學習/遊戲分離鐵則:只存學習側 ReviewPaper,絕不碰戰鬥數值。
const STORED_PAPERS_KEY = 'ef.review.papers';
const MAX_STORED_PAPERS = 5;

/** Read the stored ReviewPaper list (newest-first). Empty on missing/corrupt. */
function readStoredPapers(): ReviewPaper[] {
  try {
    const raw = localStorage.getItem(STORED_PAPERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ReviewPaper[]) : [];
  } catch {
    return [];
  }
}

/**
 * Persist a generated ReviewPaper. Dedupes by paperId (newest wins, moved to
 * front) and trims to the most-recent MAX_STORED_PAPERS. Best-effort: a
 * storage failure (quota / private mode) is swallowed so it never blocks the
 * print flow.
 */
export function storeReviewPaper(paper: ReviewPaper): void {
  if (!paper || typeof paper.paperId !== 'string') return;
  try {
    const existing = readStoredPapers().filter((p) => p.paperId !== paper.paperId);
    const next = [paper, ...existing].slice(0, MAX_STORED_PAPERS);
    localStorage.setItem(STORED_PAPERS_KEY, JSON.stringify(next));
  } catch {
    // ignore — storing is a convenience, not load-bearing for printing.
  }
}

/**
 * Load a stored ReviewPaper by id, or the most-recent one when `paperId` is
 * omitted. Returns null when nothing is stored / the id is unknown.
 */
export function loadStoredPaper(paperId?: string): ReviewPaper | null {
  const papers = readStoredPapers();
  if (papers.length === 0) return null;
  if (!paperId) return papers[0];
  return papers.find((p) => p.paperId === paperId) ?? null;
}

/**
 * Render the review-paper entry point into `container`.
 * onDone: called when the user exits (back button).
 *
 * 簽名不變(`runReviewPaper(container, onDone)`),供 mistake-book / review-hub /
 * 既有測試沿用。
 */
export async function runReviewPaper(
  container: HTMLElement,
  onDone: () => void,
  opts: { buildFrom?: PlayerProgress } = {},
): Promise<void> {
  ensureAcademicTheme();
  ensureReviewPaperStyle();

  const root = document.createElement('div');
  root.className = 'review-paper';
  container.appendChild(root);

  renderLoading(root);

  // 時鐘 / 存檔 / 批改回流一律動「真的 progress」;`buildFrom`(例:考試範圍過濾後的
  // progress 副本)只用來決定「組哪些題進卷」,絕不存回去(避免覆蓋真資料)。
  const progress = loadProgress();
  if (!progress) {
    renderEmpty(root, onDone);
    return;
  }
  const buildProgress = opts.buildFrom ?? progress;

  let paper: PersonalReviewPaper;
  try {
    paper = await buildPersonalReviewPaper(buildProgress);
  } catch {
    renderError(root, () => void runReviewPaper(container, onDone), onDone);
    return;
  }

  const totalItems = paper.sections.reduce((n, s) => n + s.items.length, 0);
  if (totalItems === 0) {
    // 完全沒弱點(空錯題本 + 無弱 box / 全部生不出題)→ 友善提示。
    renderEmpty(root, onDone);
    return;
  }

  // 卷次驅動時鐘 +1(一張卷算一次練習,讓 box 到期節奏前進)。純學習側。
  advancePracticePaperCount(progress);
  saveProgress(progress);

  // 攤平 + 持久化,供拍照批改回流取回(沿用既有 grade-review)。
  const reviewPaper = toReviewPaper(paper);
  storeReviewPaper(reviewPaper);

  renderPaper(root, paper, onDone);
}

// ── 畫面內考卷(按科分大題 + 作答格 + 顯示解答 + 即時批改回流)─────────────────

interface FlatItem extends ReviewItem {
  /** 整卷連續題號(1..N)。 */
  num: number;
}

/** 把分科 paper 攤平成連續題號清單(畫面內逐題作答用)。 */
function flatten(paper: PersonalReviewPaper): FlatItem[] {
  const out: FlatItem[] = [];
  let n = 0;
  for (const section of paper.sections) {
    for (const item of section.items) {
      n += 1;
      out.push({ ...item, num: n });
    }
  }
  return out;
}

function renderPaper(root: HTMLElement, paper: PersonalReviewPaper, onDone: () => void): void {
  root.innerHTML = '';

  const flat = flatten(paper);
  // 作答狀態:每題是否已批改 + 是否答對(供結算 / 回流)。
  const graded: Array<{ correct: boolean } | null> = new Array(flat.length).fill(null);

  // 卷頭。
  const head = document.createElement('div');
  head.className = 'rv-head';
  head.innerHTML = `
    <p class="rv-headline">${escapeHtml(paperHeadline(paper))}</p>
    <p class="rv-meta">卷號 ${escapeHtml(paper.paperId)} · 共 ${flat.length} 題,按科目分大題。寫完可在下方逐題對答,或印出後拍照批改。</p>
    <div class="rv-head-actions">
      <button class="rv-print-btn ef-btn ef-btn--secondary" type="button">🖨 列印這張卷</button>
      <button class="rv-reveal-btn ef-btn ef-btn--secondary" type="button">顯示全部解答</button>
    </div>
  `;
  root.appendChild(head);

  // 列印:開新視窗 write 考卷 HTML。
  head.querySelector('.rv-print-btn')!.addEventListener('click', () => {
    openPrintWindow(paper);
  });

  // 分科大題。
  const sectionLabels = ['一', '二', '三', '四', '五', '六', '七', '八'];
  let runningIdx = 0;
  const allReveal: HTMLElement[] = [];

  paper.sections.forEach((section, sIdx) => {
    const sec = document.createElement('section');
    sec.className = 'rv-section';
    const sLabel = sectionLabels[sIdx] ?? String(sIdx + 1);

    const sTitle = document.createElement('h2');
    sTitle.className = 'rv-section-title';
    sTitle.textContent = `${sLabel}、${section.label}`;
    sec.appendChild(sTitle);

    section.items.forEach((item) => {
      runningIdx += 1;
      const num = runningIdx;
      const itemIndex = num - 1;

      const card = document.createElement('div');
      card.className = 'rv-item';
      card.dataset.num = String(num);

      card.innerHTML = `
        <div class="rv-item-num">第 ${num} 題</div>
        <div class="rv-item-prompt">${escapeHtml(item.prompt)}</div>
        <div class="rv-fill-row">
          <input type="text" class="rv-fill-input" placeholder="輸入答案" autocomplete="off" aria-label="第 ${num} 題答案" />
          <button class="rv-check-btn ef-btn ef-btn--primary" type="button">對答案</button>
        </div>
        <div class="rv-feedback" role="status" aria-live="polite"></div>
        <div class="rv-reveal" hidden>正解:${escapeHtml(item.displayAnswer)}${item.explanation ? ` —— ${escapeHtml(item.explanation)}` : ''}</div>
      `;
      sec.appendChild(card);

      const input = card.querySelector('.rv-fill-input') as HTMLInputElement;
      const checkBtn = card.querySelector('.rv-check-btn') as HTMLButtonElement;
      const feedback = card.querySelector('.rv-feedback') as HTMLElement;
      const reveal = card.querySelector('.rv-reveal') as HTMLElement;
      allReveal.push(reveal);

      const check = (): void => {
        if (graded[itemIndex]) return; // 已批改過,不重複入帳。
        const got = normalize(input.value);
        if (got === '') { input.focus(); return; }
        const correct = item.acceptedAnswers.includes(got);
        graded[itemIndex] = { correct };
        gradeItem(item, correct);

        if (correct) {
          feedback.className = 'rv-feedback rv-correct';
          feedback.textContent = '✓ 答對了!';
        } else {
          feedback.className = 'rv-feedback rv-wrong';
          feedback.textContent = '✗ 再看一次正解,已幫你加進錯題本下次再練。';
        }
        reveal.hidden = false;
        input.disabled = true;
        checkBtn.disabled = true;
        updateSummary();
      };

      checkBtn.addEventListener('click', check);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') check(); });
    });

    root.appendChild(sec);
  });

  // 顯示全部解答(批改 / 對照用)。
  let revealed = false;
  const revealBtn = head.querySelector('.rv-reveal-btn') as HTMLButtonElement;
  revealBtn.addEventListener('click', () => {
    revealed = !revealed;
    allReveal.forEach((r) => { r.hidden = !revealed; });
    revealBtn.textContent = revealed ? '隱藏解答' : '顯示全部解答';
  });

  // 底部結算 + 返回。
  const footer = document.createElement('div');
  footer.className = 'rv-footer';
  footer.innerHTML = `
    <p class="rv-summary" role="status" aria-live="polite"></p>
    <button class="rv-back-btn ef-btn ef-btn--secondary back-btn" type="button">← 完成,返回</button>
  `;
  root.appendChild(footer);
  footer.querySelector('.rv-back-btn')!.addEventListener('click', onDone);

  const summaryEl = footer.querySelector('.rv-summary') as HTMLElement;
  function updateSummary(): void {
    const done = graded.filter((g) => g !== null).length;
    const right = graded.filter((g) => g?.correct).length;
    summaryEl.textContent = done === 0
      ? ''
      : `已對答 ${done}/${flat.length} 題,答對 ${right} 題。`;
  }
  updateSummary();
}

/**
 * 一題的批改回流(純學習側入帳):
 *   - 答對 → `recordAttemptV2(kp,'correct')` 升 Leitner box(同時推進卷次驅動 due)。
 *   - 答錯 → `recordMistake` 回流錯題本(下次複習針對加強)+ box 降一階。
 * 只動 box / mistakes,不碰戰鬥數值 / commanderXp。失敗不阻斷 UI。
 */
function gradeItem(item: ReviewItem, correct: boolean): void {
  const progress = loadProgress();
  if (!progress) return;
  const nowISO = new Date().toISOString();
  const kp = findOrCreateKP(progress, item.kp, item.subject as Subject);

  if (correct) {
    recordAttemptV2(progress, kp, 'correct', nowISO, deriveQuestionId(item.prompt));
  } else {
    recordAttemptV2(progress, kp, 'wrong', nowISO, deriveQuestionId(item.prompt));
    recordMistake(progress, {
      questionId: deriveQuestionId(item.prompt),
      kp: item.kp,
      subject: item.subject,
      prompt: item.prompt,
      correctAnswer: item.displayAnswer,
      userAnswer: null,
      explanation: item.explanation || undefined,
    });
  }
  saveProgress(progress);
}

/** Open the printable HTML in a new window (沿用 print pattern). */
function openPrintWindow(paper: PersonalReviewPaper): void {
  const html = generatePersonalReviewHtml(paper);
  const w = window.open('', '_blank');
  if (!w) {
    alert('請允許彈出視窗才能列印複習考卷');
    return;
  }
  w.document.write(html);
  w.document.close();
}

// ── views(學術風格,純文字、極簡)──────────────────────────────────────────

function renderLoading(root: HTMLElement): void {
  root.innerHTML = `
    <div class="review-paper-loading">
      <p>正在依你的弱點出題…</p>
    </div>
  `;
}

function renderEmpty(root: HTMLElement, onDone: () => void): void {
  renderMessage(
    root,
    '目前沒有需要複習的弱點,先去「真題練習」或上傳考卷累積一些題目,系統就會幫你出個人化複習卷。',
    onDone,
  );
}

function renderMessage(root: HTMLElement, msg: string, onDone: () => void): void {
  root.innerHTML = `
    <div class="review-paper-msg">
      <p>${escapeHtml(msg)}</p>
      <button class="back-btn ef-btn ef-btn--secondary">← 返回</button>
    </div>
  `;
  root.querySelector('.back-btn')!.addEventListener('click', onDone);
}

function renderError(
  root: HTMLElement,
  onRetry: () => void,
  onDone: () => void,
): void {
  root.innerHTML = `
    <div class="review-paper-error">
      <p>出題失敗,請稍後再試一次。</p>
      <div class="review-paper-actions">
        <button class="retry-btn ef-btn ef-btn--primary">重試</button>
        <button class="back-btn ef-btn ef-btn--secondary">← 返回</button>
      </div>
    </div>
  `;
  root.querySelector('.retry-btn')!.addEventListener('click', onRetry);
  root.querySelector('.back-btn')!.addEventListener('click', onDone);
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** 寬鬆比對:去頭尾 / 全形空白 + 轉小寫(與 mistake-similar.normalizeAnswer 一致)。 */
function normalize(raw: string): string {
  return String(raw).trim().replace(/\s+/g, '').toLowerCase();
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

/** 複習卷 scoped 樣式(只用 --ef- token,不碰全域 styles.css)。冪等注入。 */
const REVIEW_PAPER_STYLE_ID = 'ef-review-paper-style';
function ensureReviewPaperStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(REVIEW_PAPER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = REVIEW_PAPER_STYLE_ID;
  style.textContent = `
.review-paper { display:flex; flex-direction:column; gap:18px; color:var(--ef-ink-soft); font-family:var(--ef-sans); }
.review-paper .review-paper-loading p,
.review-paper .review-paper-msg p,
.review-paper .review-paper-error p { margin:0 0 12px; font-size:15px; color:var(--ef-sub); }
.review-paper .review-paper-actions { display:flex; gap:10px; flex-wrap:wrap; }

.review-paper .rv-head { display:flex; flex-direction:column; gap:6px; padding-bottom:12px; border-bottom:1px solid var(--ef-line); }
.review-paper .rv-headline { margin:0; font-family:var(--ef-serif); font-weight:700; font-size:20px; color:var(--ef-ink); }
.review-paper .rv-meta { margin:0; font-size:13px; color:var(--ef-sub); line-height:1.6; }
.review-paper .rv-head-actions { display:flex; gap:10px; flex-wrap:wrap; margin-top:6px; }

.review-paper .rv-section { display:flex; flex-direction:column; gap:12px; }
.review-paper .rv-section-title {
  margin:8px 0 2px; font-family:var(--ef-serif); font-weight:700; font-size:17px; color:var(--ef-ink);
  border-left:4px solid var(--ef-green); padding-left:10px; letter-spacing:0.5px;
}
.review-paper .rv-item {
  display:flex; flex-direction:column; gap:10px;
  padding:16px; background:var(--ef-paper-bright); border:1px solid var(--ef-line);
  border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card);
}
.review-paper .rv-item-num { font-size:12px; font-weight:600; color:var(--ef-sub); letter-spacing:0.5px; }
.review-paper .rv-item-prompt { font-family:var(--ef-serif); font-size:17px; line-height:1.55; color:var(--ef-ink); }
.review-paper .rv-fill-row { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.review-paper .rv-fill-input {
  flex:1 1 160px; min-height:44px; padding:10px 14px; border:1px solid var(--ef-line);
  border-radius:var(--ef-radius-sm); background:#fff; font-family:var(--ef-sans); font-size:16px; color:var(--ef-ink);
}
.review-paper .rv-fill-input:focus-visible { outline:3px solid rgba(31,111,92,0.35); outline-offset:1px; border-color:var(--ef-green); }
.review-paper .rv-fill-input:disabled { background:var(--ef-paper); color:var(--ef-sub); }
.review-paper .rv-feedback { font-size:14px; min-height:1px; }
.review-paper .rv-feedback.rv-correct { color:var(--ef-green-dark); font-weight:600; }
.review-paper .rv-feedback.rv-wrong { color:#b3261e; }
.review-paper .rv-reveal {
  font-size:14px; line-height:1.6; color:var(--ef-ink-soft);
  padding:10px 12px; background:var(--ef-note); border-radius:var(--ef-radius-sm);
}

.review-paper .rv-footer { display:flex; flex-direction:column; gap:10px; padding-top:12px; border-top:1px solid var(--ef-line); }
.review-paper .rv-summary { margin:0; font-size:14px; color:var(--ef-ink-soft); font-weight:600; }
.review-paper .rv-back-btn { align-self:flex-start; }
`;
  document.head.appendChild(style);
}
